import clsx from 'clsx';
import { CloudRain, Wind, Thermometer, Droplets } from 'lucide-react';

export interface WeatherImpactData {
  temperature: number;
  feelsLike?: number;
  windSpeed: number;
  windDirection: string;
  condition: string;
  precipitationProbability?: number;
  humidity?: number;
  windEffect?: 'boosting' | 'neutral' | 'suppressing';
  rainoutRisk?: 'none' | 'low' | 'moderate' | 'high';
  runEnvironmentAdjustment?: number;
  intelligenceSummary?: string;
}

interface Props {
  weather: WeatherImpactData;
  isOutdoor?: boolean;
}

const WIND_EFFECT_CONFIG = {
  boosting:    { label: 'Wind Boosting Offense', color: 'text-warn' },
  neutral:     { label: 'Wind Neutral',           color: 'text-gray-400' },
  suppressing: { label: 'Wind Suppressing Runs',  color: 'text-brand' },
} as const;

const RAINOUT_CONFIG = {
  none:     { label: 'No Rain Risk',   color: 'text-win',  show: false },
  low:      { label: 'Low Rain Risk',  color: 'text-gray-400', show: true },
  moderate: { label: 'Rain Possible',  color: 'text-warn', show: true },
  high:     { label: 'Rainout Risk',   color: 'text-loss', show: true },
} as const;

function tempColor(temp: number): string {
  if (temp >= 85) return 'text-loss';
  if (temp >= 72) return 'text-warn';
  if (temp >= 55) return 'text-gray-200';
  return 'text-brand';
}

export function WeatherImpact({ weather, isOutdoor = true }: Props) {
  const windEffect = weather.windEffect ?? 'neutral';
  const rainoutRisk = weather.rainoutRisk ?? 'none';
  const rainConfig = RAINOUT_CONFIG[rainoutRisk];
  const windConfig = WIND_EFFECT_CONFIG[windEffect];

  const runAdj = weather.runEnvironmentAdjustment;
  const hasAdjustment = runAdj != null && Math.abs(runAdj - 1.0) > 0.01;

  return (
    <div className="card p-4 space-y-4">
      <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">Weather Impact</div>

      {!isOutdoor && (
        <div className="text-xs text-gray-600 italic">Indoor venue — weather has minimal effect.</div>
      )}

      <div className="grid grid-cols-2 gap-3">
        {/* Temperature */}
        <div className="flex items-center gap-2">
          <Thermometer className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <div>
            <div className={clsx('text-sm font-semibold', tempColor(weather.temperature))}>
              {weather.temperature}°F
              {weather.feelsLike != null && Math.abs(weather.feelsLike - weather.temperature) >= 3 && (
                <span className="text-gray-500 text-xs ml-1">feels {weather.feelsLike}°</span>
              )}
            </div>
            <div className="text-[10px] text-gray-500">Temperature</div>
          </div>
        </div>

        {/* Wind */}
        <div className="flex items-center gap-2">
          <Wind className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <div>
            <div className={clsx('text-sm font-semibold', windEffect === 'boosting' ? 'text-warn' : windEffect === 'suppressing' ? 'text-brand' : 'text-gray-200')}>
              {weather.windSpeed} mph {weather.windDirection}
            </div>
            <div className={clsx('text-[10px]', windConfig.color)}>{windConfig.label}</div>
          </div>
        </div>

        {/* Condition */}
        <div className="flex items-center gap-2">
          <CloudRain className={clsx('w-4 h-4 flex-shrink-0', rainConfig.show ? 'text-warn' : 'text-gray-500')} />
          <div>
            <div className="text-sm font-semibold text-gray-200">{weather.condition}</div>
            {rainConfig.show && (
              <div className={clsx('text-[10px]', rainConfig.color)}>
                {rainConfig.label}
                {weather.precipitationProbability != null && ` · ${weather.precipitationProbability}%`}
              </div>
            )}
            {!rainConfig.show && <div className="text-[10px] text-gray-500">Clear conditions</div>}
          </div>
        </div>

        {/* Humidity */}
        {weather.humidity != null && (
          <div className="flex items-center gap-2">
            <Droplets className="w-4 h-4 text-gray-500 flex-shrink-0" />
            <div>
              <div className="text-sm font-semibold text-gray-200">{weather.humidity}%</div>
              <div className="text-[10px] text-gray-500">Humidity</div>
            </div>
          </div>
        )}
      </div>

      {/* Run environment adjustment */}
      {hasAdjustment && runAdj != null && (
        <div className={clsx(
          'pt-3 border-t border-surface-4 flex items-center justify-between',
        )}>
          <span className="text-xs text-gray-400">Run Env. Adjustment</span>
          <span className={clsx(
            'text-xs font-mono font-semibold',
            runAdj >= 1.05 ? 'text-warn' : runAdj <= 0.95 ? 'text-brand' : 'text-gray-300',
          )}>
            {runAdj >= 1.0 ? '+' : ''}{((runAdj - 1.0) * 100).toFixed(1)}%
          </span>
        </div>
      )}

      {/* Summary */}
      {weather.intelligenceSummary && (
        <div className="pt-2 border-t border-surface-4 text-xs text-gray-400 leading-relaxed">
          {weather.intelligenceSummary}
        </div>
      )}
    </div>
  );
}
